import type { NegotiationVariable } from '../core/define'
import { defineObjectGroup } from '../core/define-object-group'

/**
 * CLIENTE PRINCIPAL (titular) de la negociación, como escalares.
 *
 * A diferencia de la colección `clientes` (loop, cantidad variable, individuales y jurídicos
 * mezclados), el titular SIEMPRE existe: es el dueño por defecto. El orquestador lo arma desde el
 * dueño por defecto y lo deja en `ctx.titular` (camino central → todos los docs).
 *
 * CÓMO SE RESUELVE SEGÚN EL TIPO DE CLIENTE (clave para el usuario):
 *  - INDIVIDUAL: los datos personales son del propio cliente. figura = 'propia'. Los
 *    campos de nombramiento (entidad, cargo, registro, notario…) quedan vacíos.
 *  - JURÍDICO: una empresa no tiene edad/DPI/género → los datos personales son del
 *    REPRESENTANTE LEGAL por defecto (la persona que actúa). `entidad` = razón social, y
 *    se llenan los campos del nombramiento. figura = 'juridica'.
 *
 * El metadato —etiquetas, tipos y notas— está en
 * `@idc-forms/schemas/variables/document/titular.variables`; los dominios cerrados de tipo de
 * cliente, género, país, departamento y plazo los aporta el host, porque salen de los MISMOS
 * catálogos canónicos que usan los formularios. Nunca se hardcodean.
 */
const t =
  (key: string) =>
  (c: { titular?: Record<string, string> | null }): string =>
    c.titular?.[key] ?? ''

export const TITULAR_VARIABLES: NegotiationVariable[] = defineObjectGroup('titular', {
  // ── Tipo de cliente (para comparar y ramificar el documento) ──
  tipo_cliente: t('tipo_cliente'),

  // ── Persona (individual: el cliente; jurídico: el representante legal) ──
  nombre: t('nombre'),
  dpi_numeros: t('dpi_numeros'),
  dpi_letras: t('dpi_letras'),
  edad: t('edad'),
  genero: t('genero'),
  estado_civil: t('estado_civil'),
  nacionalidad: t('nacionalidad'),
  profesion: t('profesion'),
  pais: t('pais'),
  departamento: t('departamento'),
  fecha_nacimiento: t('fecha_nacimiento'),

  // ── Contacto ──
  nit: t('nit'),
  email: t('email'),
  telefono: t('telefono'),
  residencia_a_su_nombre: t('residencia_a_su_nombre'),
  residencia_justificacion: t('residencia_justificacion'),

  // ── Cómo comparece ──
  figura: t('figura'),

  // ── Entidad + nombramiento del representante (solo cliente jurídico) ──
  entidad: t('entidad'),
  cargo: t('cargo'),
  nombramiento_fecha: t('nombramiento_fecha'),
  nombramiento_fecha_final: t('nombramiento_fecha_final'),
  tipo_plazo: t('tipo_plazo'),
  registro_nombre: t('registro_nombre'),
  registro_numero: t('registro_numero'),
  registro_folio: t('registro_folio'),
  registro_libro: t('registro_libro'),
  notario_nombre: t('notario_nombre'),
  notario_tratamiento: t('notario_tratamiento')
})
